import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { EmptyState } from "./InventoryHome";
import { Boxes, Package } from "lucide-react";

/** Stock tab — pick a site, see what's held there. */
export default function StockTab({ sites, items, refreshTick }) {
  const [site, setSite] = useState(sites[0] || "");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [assetValue, setAssetValue] = useState(null);

  useEffect(() => {
    if (!sites.length) return;
    if (!site || !sites.includes(site)) setSite(sites[0]);
  }, [sites, site]);

  useEffect(() => {
    if (!site) return;
    let cancel = false;
    setLoading(true);
    Promise.all([
      api.get("/inventory/stock", { params: { site } }),
      api.get("/inventory/asset-value", { params: { site } }),
    ])
      .then(([sr, ar]) => {
        if (cancel) return;
        setRows(sr.data || []);
        const me = (ar.data?.per_site || []).find((p) => p.site === site);
        setAssetValue(me || null);
      })
      .catch(() => { if (!cancel) { setRows([]); setAssetValue(null); } })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [site, refreshTick]);

  const totalQty = useMemo(() => rows.reduce((acc, r) => acc + (r.quantity || 0), 0), [rows]);
  const hasItems = items.length > 0;

  if (!hasItems) {
    return (
      <EmptyState
        testid="stock-empty-no-items"
        icon={Package}
        title="No items in the catalogue"
        body="Add an item from the toolbar above. You'll be able to seed initial stock per site at the same time."
      />
    );
  }

  return (
    <div className="bg-white border border-slate-200 rounded-2xl">
      <div className="p-4 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-medium uppercase tracking-wider text-slate-500">Site</span>
          <select
            data-testid="stock-site-picker"
            value={site}
            onChange={(e) => setSite(e.target.value)}
            className="rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-olive/30"
          >
            {sites.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-3 text-xs text-slate-500">
          {assetValue && (
            <span
              data-testid="stock-site-asset-value"
              className="inline-flex items-baseline gap-1.5 rounded-full bg-olive/10 border border-olive/30 px-3 py-1 text-olive font-semibold text-[11px]"
              title="Current stock valuation at this site"
            >
              <span className="opacity-70 font-normal">Asset value</span>
              <span className="tabular-nums">₹{Number(assetValue.value).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              {assetValue.unrated_qty > 0 && (
                <span className="ml-1 text-amber-700 font-medium" title={`${assetValue.unrated_item_count} item(s) unrated at this site`}>
                  · {assetValue.unrated_qty} unrated
                </span>
              )}
            </span>
          )}
          <span>{loading ? "Loading…" : `${rows.length} item${rows.length === 1 ? "" : "s"} · ${totalQty} units total`}</span>
        </div>
      </div>

      {loading ? (
        <div className="p-10 text-center text-sm text-slate-500">Loading stock…</div>
      ) : rows.length === 0 ? (
        <div className="p-10 text-center" data-testid="stock-empty">
          <Boxes className="mx-auto text-slate-300" size={28} strokeWidth={1.5} />
          <div className="text-sm font-medium text-slate-900 mt-2">No stock at {site}</div>
          <p className="text-xs text-slate-500 mt-1">
            Use <span className="font-medium">Adjust stock</span> from the toolbar to record what&apos;s on hand at this site.
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto" data-testid="stock-table-wrap">
          <table className="w-full text-sm" data-testid="stock-table">
            <thead>
              <tr className="text-[11px] font-medium uppercase tracking-wider text-slate-500 bg-slate-50/60">
                <th className="text-left px-4 py-2.5">Item</th>
                <th className="text-left px-4 py-2.5 hidden sm:table-cell">Category</th>
                <th className="text-left px-4 py-2.5 hidden md:table-cell">Unit</th>
                <th className="text-right px-4 py-2.5">Qty</th>
                <th className="text-right px-4 py-2.5 hidden lg:table-cell">Last move</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => (
                <tr key={r.item_id} data-testid={`stock-row-${r.item_id}`} className="hover:bg-slate-50/60">
                  <td className="px-4 py-3 font-medium text-slate-900">{r.item_name || r.item_id}</td>
                  <td className="px-4 py-3 text-slate-500 hidden sm:table-cell">{r.category || "—"}</td>
                  <td className="px-4 py-3 text-slate-500 hidden md:table-cell">{r.unit || "—"}</td>
                  <td className="px-4 py-3 text-right font-semibold tabular-nums">{r.quantity}</td>
                  <td className="px-4 py-3 text-right text-[11px] text-slate-400 hidden lg:table-cell">
                    {r.last_movement_at ? new Date(r.last_movement_at).toLocaleString() : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
